<template>
  <b-card class="rounded-lg">
    <report-header
      :title="$t('ledger_rep_pack')"
      :scope-label="scopeLabel"
      :can-company="canCompany"
      :company="company"
      @company="company = $event; load()"
      @print="printReport"
    />
    <div class="d-flex flex-wrap mb-1">
      <b-form-input v-model.number="year" type="number" class="mr-50 mb-50 ledger-report-year" />
      <b-form-input v-model.number="month" type="number" min="1" max="12" class="mr-50 mb-50 ledger-report-year" />
      <b-button size="sm" variant="primary" @click="load">{{ $t('ledger_assets_load') }}</b-button>
    </div>
    <div v-if="loading" class="text-center py-3"><b-spinner /></div>
    <template v-else-if="report">
      <b-alert
        v-if="report.close_status"
        show
        :variant="closeVariant"
      >
        {{ closeBanner }}
      </b-alert>
      <b-card class="mb-1">
        <h5>{{ $t('ledger_tb_title') }}</h5>
        <div v-for="row in trialAccounts" :key="row.code" class="d-flex justify-content-between small">
          <span>{{ row.code }}</span>
          <span class="ledger-num">{{ money(row.debit) }} / {{ money(row.credit) }}</span>
        </div>
      </b-card>
      <b-card class="mb-1">
        <h5>{{ $t('ledger_rep_profit') }}</h5>
        <div v-if="report.profit_position && report.profit_position.totals" class="ledger-num">
          {{ money(report.profit_position.totals.net_profit) }}
        </div>
      </b-card>
      <b-card class="mb-1">
        <button
          type="button"
          class="fp-pack-toggle"
          @click="packSheetOpen = !packSheetOpen"
        >
          <feather-icon
            :icon="packSheetOpen ? 'ChevronDownIcon' : 'ChevronLeftIcon'"
            size="16"
          />
          <h5 class="mb-0">
            {{ $t('ledger_fp_pack') }}
          </h5>
        </button>
        <div
          v-show="packSheetOpen"
          class="fp-pack-sheet mt-1"
        >
          <financial-position-sheet
            v-if="report.financial_position"
            :report="report.financial_position"
            :show-comparative="false"
          />
        </div>
      </b-card>
      <b-card class="mb-1">
        <h5>{{ $t('ledger_rep_centers') }}</h5>
        <div v-for="center in (report.profitability && report.profitability.centers) || []" :key="center.key">
          {{ center.label_ar || center.label }}
          <span class="ledger-num"> {{ money(center.gross_profit) }}</span>
        </div>
      </b-card>
      <b-card class="mb-1">
        <h5>{{ $t('ledger_maps_title') }}</h5>
        <p v-if="report.map_stock && report.map_stock.company_level" class="text-muted small">{{ $t('ledger_rep_company_level') }}</p>
        <div v-if="report.map_stock && report.map_stock.totals" class="ledger-num">
          {{ money(report.map_stock.totals.value) }}
        </div>
      </b-card>
      <b-card class="mb-1">
        <h5>{{ $t('ledger_depr_title') }}</h5>
        <p v-if="report.depreciation && report.depreciation.company_level" class="text-muted small">{{ $t('ledger_rep_company_level') }}</p>
        <b-alert v-if="report.depreciation && !report.depreciation.run" show variant="warning">
          {{ $t('ledger_rep_no_depr') }}
          <b-link :to="{ name: 'ledger-depreciation' }">{{ $t('ledger_depr_title') }}</b-link>
        </b-alert>
        <div v-else-if="report.depreciation && report.depreciation.run" class="ledger-num">
          {{ money(report.depreciation.run.total_amount) }}
        </div>
      </b-card>
      <b-card class="mb-1">
        <h5>{{ $t('ledger_rec_title') }}</h5>
        <p
          v-if="report.contract_recognition && report.contract_recognition.company_level"
          class="text-muted small"
        >
          {{ $t('ledger_rep_company_level') }}
        </p>
        <b-alert
          v-if="report.contract_recognition && !report.contract_recognition.run"
          show
          variant="warning"
        >
          {{ $t('ledger_rec_no_run') }}
          <b-link
            v-if="hasPermission('view contracts')"
            :to="{ name: 'ledger-contract-recognition' }"
          >
            {{ $t('ledger_rec_title') }}
          </b-link>
        </b-alert>
        <div
          v-else-if="report.contract_recognition && report.contract_recognition.run"
          class="ledger-num"
        >
          {{ money(report.contract_recognition.run.total_revenue) }}
        </div>
      </b-card>
      <b-card class="mb-1">
        <h5>{{ $t('ledger_acc_title') }}</h5>
        <div
          v-for="row in (report.revenue_accruals && report.revenue_accruals.accruals) || []"
          :key="row.id"
          class="d-flex justify-content-between"
        >
          <span>{{ row.project_number }} {{ row.project_name }}</span>
          <span class="ledger-num">{{ money(row.amount) }}</span>
        </div>
        <div
          v-if="report.revenue_accruals"
          class="ledger-num"
        >
          {{ $t('ledger_assets_total') }} {{ money(report.revenue_accruals.total) }}
        </div>
      </b-card>
      <b-card>
        <h5>{{ $t('ledger_payroll_title') }}</h5>
        <b-alert v-if="report.payroll && !report.payroll.run" show variant="warning">{{ $t('ledger_rep_no_payroll') }}</b-alert>
        <div v-else-if="report.payroll && report.payroll.totals" class="ledger-num">
          {{ $t('ledger_payroll_net') }} {{ money(report.payroll.totals.net) }}
        </div>
      </b-card>
    </template>
  </b-card>
</template>

<script>
import { BCard, BFormInput, BButton, BSpinner, BAlert, BLink } from 'bootstrap-vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import { getMonthlyPack } from '@/api/accounting/financialReports'
import { oneFrom } from '@/utils/ledger'
import ReportHeader from './ReportHeader.vue'
import FinancialPositionSheet from './FinancialPositionSheet.vue'
import reportMixin from './reportMixin'

export default {
  name: 'MonthlyPack',
  components: {
    BCard, BFormInput, BButton, BSpinner, BAlert, BLink, FeatherIcon, ReportHeader, FinancialPositionSheet,
  },
  mixins: [reportMixin],
  data() {
    return { packSheetOpen: false }
  },
  computed: {
    trialAccounts() {
      const tb = this.report && this.report.trial_balance
      if (!tb) return []
      return Array.isArray(tb.accounts) ? tb.accounts : []
    },
    closeStatus() {
      return this.report && this.report.close_status
    },
    closeVariant() {
      if (!this.closeStatus) return 'secondary'
      if (this.closeStatus.close_forced) return 'warning'
      return this.closeStatus.status === 'closed' ? 'success' : 'danger'
    },
    closeBanner() {
      const status = this.closeStatus
      if (!status) return ''
      if (status.close_forced) return this.$t('ledger_close_pack_forced', { reason: status.close_reason || '' })
      if (status.status === 'closed') return this.$t('ledger_close_pack_closed')
      return this.$t('ledger_close_pack_open', { count: status.blocking_missing || 0 })
    },
  },
  mounted() { this.load() },
  methods: {
    async load() {
      this.loading = true
      try {
        const res = await getMonthlyPack({ year: this.year, month: this.month, ...this.scopeParams })
        this.report = oneFrom(res)
      } catch (error) {
        this.toastError(error)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.ledger-num { font-variant-numeric: tabular-nums; }
.ledger-report-year { max-width: 7rem; }
.fp-pack-toggle {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  width: 100%;
  border: 0;
  background: transparent;
  padding: 0;
  text-align: inherit;
  color: inherit;
}
@media print {
  .fp-pack-sheet { display: block !important; }
}
</style>
